import { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'

import { TransferPatientToInstitute } from './TransferPatientToInstitute'
import { useUpdateQueryParam } from '../../../components/ClinPagination/ClinPagination.model'
import { StepState } from '../../../components/ClinStepper/ClinStepper'
import { useAppContext } from '../../../context/app'
import { useRedirectIfPtaTransferBlocked } from '../../Patients/PatientDetail/ptaTransferGate'
import { AnalyticsEvent } from '../../../services/Analytics'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  getPatientDetail,
  transferPatientInstitute
} from '../../../services/ApiService'
import {
  NewInstituteDetilsDto,
  NewPhysicianDetilsDto,
  PatientDetailsDto,
  PatientProgramDto
} from '../../../types/swaggerTypes'
import { useCountries } from '../../../utils/useCountries'
import { handlePatientNotFound } from '../../../utils/handlePatientNotFound'
import { useErrorMessage } from '../../../utils/useErrorMessage'
import {
  PatientTransferSteps,
  patientSteps,
  defaultPhysicianParams
} from '../models/TransferPatientContainer.model'

interface ITransferPatientRouteParams {
  patientId: string
  physicianId: string
}

interface ITransferPatientProps
  extends RouteComponentProps<ITransferPatientRouteParams> {}

export const TransferPatientToInstituteContainer: FunctionComponent<
  ITransferPatientProps
> = ({ match, history }) => {
  const { t } = useTranslation()
  const { dispatch, defaultShippingAddress, portfolioCountryCode } =
    useAppContext()
  const { patientId, physicianId } = match.params
  const [patient, setPatient] = useState<PatientDetailsDto>()
  const [program, setProgram] = useState<PatientProgramDto>()
  const [isPatientLoading, setIsPatientLoading] = useState<boolean>(true)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const submissionInProgress = useRef(false)
  const [currentStep, setCurrentStep] = useState<number>(
    PatientTransferSteps.STEP1
  )
  const [physicianDetails, setPhysicianDetails] =
    useState<NewPhysicianDetilsDto>(defaultPhysicianParams)
  const [isAgreementConfirmed, setIsAgreementConfirmed] =
    useState<boolean>(false)

  useRedirectIfPtaTransferBlocked(
    patientId,
    physicianId,
    history,
    defaultShippingAddress,
    portfolioCountryCode
  )

  const { isCountriesLoading, countries } = useCountries()

  //Needed only for Browser Back/Forward buttons work
  const setupURLparams = () => {
    history.push(
      `/programs/my-physicians/${physicianId}/${patientId}/transfer-patient/institute?step=${currentStep}`
    )
  }

  const handleCancel = () => {
    history.push(`/programs/my-physicians/${physicianId}/${patientId}`)
  }

  const getSteppers = (): StepState[] | undefined => {
    if (currentStep !== null) {
      return patientSteps[currentStep]
    }
  }

  const handleContinue = (step: number) => {
    setupURLparams()
    setCurrentStep(step)
  }

  const savePhysicianDetails = (data: NewPhysicianDetilsDto) => {
    setPhysicianDetails(data)
    setupURLparams()
    setCurrentStep(PatientTransferSteps.STEP3)
  }

  const handleSubmitError = useErrorMessage(
    t('transfer_patient_to_institute:submit_error')
  )

  const handleGetPatientDetailError = useErrorMessage(
    t('transfer_patient_to_institute:patient_detail_error')
  )

  const submitTransferring = (instituteDetails: NewInstituteDetilsDto) => {
    if (submissionInProgress.current) {
      return
    }
    submissionInProgress.current = true
    setIsSubmitting(true)
    return transferPatientInstitute(physicianId, patientId, {
      ...physicianDetails,
      ...instituteDetails
    })
      .then((response) => {
        if (response.status >= 200 && response.status <= 299) {
          setCurrentStep(PatientTransferSteps.STEP4)
          analyticsServiceSingleton.trackEvent(
            AnalyticsEvent.PatientTransferInstitute,
            {
              patientId,
              physicianId,
              instituteName: instituteDetails.newInstituteName
            }
          )
        }
      })
      .catch((error: AxiosError) => handleSubmitError(error))
      .finally(() => {
        submissionInProgress.current = false
        setIsSubmitting(false)
      })
  }

  useUpdateQueryParam({ step: currentStep })

  useEffect(() => {
    patientId &&
      physicianId &&
      getPatientDetail(patientId, physicianId)
        .then((response) => {
          setPatient(response.data)
          setProgram(response.data.program ?? undefined)
        })
        .catch((error: AxiosError) => {
          if (!handlePatientNotFound(error, dispatch, history)) {
            handleGetPatientDetailError(error)
          }
        })
        .finally(() => setIsPatientLoading(false))

    const unListen = history.listen(() => {
      const urlSearchParams = new URLSearchParams(history.location.search)
      const step: number = Number(urlSearchParams.get('step'))
      setCurrentStep(step)
    })

    return () => {
      unListen()
    }
  }, [dispatch, handleGetPatientDetailError, patientId, physicianId, history])

  return (
    <TransferPatientToInstitute
      isLoading={isPatientLoading && isCountriesLoading}
      isSubmitting={isSubmitting}
      patient={patient}
      program={program}
      physicianDetails={physicianDetails}
      currentStep={currentStep}
      isAgreementConfirmed={isAgreementConfirmed}
      countries={countries}
      setIsAgreementConfirmed={setIsAgreementConfirmed}
      selectedSteppers={getSteppers()}
      handleCancel={handleCancel}
      handleContinue={handleContinue}
      savePhysicianDetails={savePhysicianDetails}
      submitTransferring={submitTransferring}
      handleViewPatientDetails={handleCancel}
    />
  )
}
